import { useState, useEffect } from "react";
import { loadHistory, deleteSession, clearHistory, formatDate, formatTime, formatRelative } from "../utils/history";
import { PASS_THRESHOLD, SECTION_META } from "../data/index";
import QuestionCard from "./QuestionCard";

export default function HistoryScreen({ onHome, onRetry }) {
  const [history, setHistory]       = useState([]);
  const [selected, setSelected]     = useState(null); // session object
  const [filter, setFilter]         = useState("all");
  const [answerFilter, setAnswerFilter] = useState("incorrect");
  const [confirmClear, setConfirmClear] = useState(false);

  useEffect(() => {
    setHistory(loadHistory());
  }, []);

  // ── DELETE one session ──────────────────────────────────────────────────────
  const handleDelete = (id, e) => {
    e.stopPropagation();
    deleteSession(id);
    setHistory(loadHistory());
    if (selected?.id === id) setSelected(null);
  };

  // ── CLEAR ALL ───────────────────────────────────────────────────────────────
  const handleClearAll = () => {
    clearHistory();
    setHistory([]);
    setSelected(null);
    setConfirmClear(false);
  };

  // ── SECTION FILTER (for list view) ─────────────────────────────────────────
  const sections = ["all", ...new Set(history.map((s) => s.section))];
  const displayed = filter === "all" ? history : history.filter((s) => s.section === filter);

  // ── DETAIL VIEW ─────────────────────────────────────────────────────────────
  if (selected) {
    return (
      <HistoryDetail
        session={selected}
        answerFilter={answerFilter}
        setAnswerFilter={setAnswerFilter}
        onBack={() => { setSelected(null); setAnswerFilter("incorrect"); }}
        onRetry={() => onRetry(selected.section, selected.mode)}
        onHome={onHome}
      />
    );
  }

  // ── LIST VIEW ───────────────────────────────────────────────────────────────
  return (
    <div className="history-screen">
      <div className="history-header">
        <button className="btn btn-outline btn-sm" onClick={onHome}>← Home</button>
        <div className="history-title-block">
          <h2>📜 My History</h2>
          <span className="history-count">{history.length} session{history.length !== 1 ? "s" : ""} saved</span>
        </div>
        {history.length > 0 && (
          <button className="btn btn-sm btn-danger-outline" onClick={() => setConfirmClear(true)}>
            🗑 Clear All
          </button>
        )}
      </div>

      {/* Confirm clear dialog */}
      {confirmClear && (
        <div className="confirm-overlay">
          <div className="confirm-box">
            <p>Delete all {history.length} saved sessions?</p>
            <p className="confirm-sub">This cannot be undone.</p>
            <div className="confirm-actions">
              <button className="btn btn-danger" onClick={handleClearAll}>Yes, delete all</button>
              <button className="btn btn-outline" onClick={() => setConfirmClear(false)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {history.length === 0 ? (
        <div className="history-empty">
          <div className="history-empty-icon">📋</div>
          <h3>No history yet</h3>
          <p>Complete a Practice or Exam session and your results will be saved here automatically.</p>
          <button className="btn btn-primary" onClick={onHome}>← Go Study</button>
        </div>
      ) : (
        <>
          {/* Section filter tabs */}
          {sections.length > 2 && (
            <div className="history-filters">
              {sections.map((s) => (
                <button
                  key={s}
                  className={`filter-btn ${filter === s ? "filter-active" : ""}`}
                  onClick={() => setFilter(s)}
                >
                  {s === "all" ? "All sections" : s}
                  <span className="filter-count">
                    {s === "all" ? history.length : history.filter((h) => h.section === s).length}
                  </span>
                </button>
              ))}
            </div>
          )}

          <div className="history-list">
            {displayed.map((session) => {
              const meta    = SECTION_META[session.section] ?? {};
              const passed  = session.pct >= PASS_THRESHOLD;
              const correct = session.correct;
              const wrong   = session.total - correct;

              return (
                <div
                  key={session.id}
                  className={`history-card ${passed ? "hc-pass" : "hc-fail"}`}
                  onClick={() => setSelected(session)}
                >
                  {/* Left accent bar */}
                  <div className="hc-accent" style={{ background: passed ? "#16a34a" : "#dc2626" }} />

                  {/* Section icon + name */}
                  <div className="hc-section">
                    <span className="hc-icon">{meta.icon ?? "📋"}</span>
                    <div>
                      <span className="hc-name" style={{ color: meta.color ?? "#374151" }}>
                        {session.section}
                      </span>
                      <span className={`hc-mode-pill ${session.mode === "exam" ? "exam-pill" : "practice-pill"}`}>
                        {session.mode === "exam" ? "🎯 Exam" : "📚 Practice"}
                      </span>
                    </div>
                  </div>

                  {/* Score */}
                  <div className="hc-score">
                    <span className="hc-pct" style={{ color: passed ? "#16a34a" : "#dc2626" }}>
                      {session.pct}%
                    </span>
                    <span className={`hc-badge ${passed ? "hc-pass-badge" : "hc-fail-badge"}`}>
                      {passed ? "PASS" : "FAIL"}
                    </span>
                  </div>

                  {/* Stats */}
                  <div className="hc-stats">
                    <span className="hc-stat-correct">✓ {correct}</span>
                    <span className="hc-stat-wrong">✗ {wrong}</span>
                    <span className="hc-stat-total">/ {session.total}</span>
                  </div>

                  {/* Date */}
                  <div className="hc-date">
                    <span>{formatDate(session.date)}</span>
                    <span className="hc-time">{formatTime(session.date)}</span>
                    <span className="hc-relative">{formatRelative(session.date)}</span>
                  </div>

                  {/* Actions */}
                  <div className="hc-actions">
                    <button className="hc-view-btn">View →</button>
                    <button
                      className="hc-delete-btn"
                      onClick={(e) => handleDelete(session.id, e)}
                      title="Delete this session"
                    >
                      🗑
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}

// ── DETAIL VIEW COMPONENT ────────────────────────────────────────────────────
function HistoryDetail({ session, answerFilter, setAnswerFilter, onBack, onRetry, onHome }) {
  const { section, mode, date, pct, correct, total, questions, answers } = session;
  const meta      = SECTION_META[section] ?? {};
  const passed    = pct >= PASS_THRESHOLD;
  const unanswered = total - Object.keys(answers).length;
  const incorrect  = total - correct - unanswered;

  const counts = { all: total, incorrect, correct, skipped: unanswered };

  const filtered = questions.filter((q, i) => {
    const isCorrect = answers[i] === q.answer;
    const skipped   = answers[i] === undefined;
    if (answerFilter === "incorrect") return !isCorrect && !skipped;
    if (answerFilter === "correct")   return isCorrect;
    if (answerFilter === "skipped")   return skipped;
    return true;
  });

  return (
    <div className="results-screen">

      {/* Navigation */}
      <div className="history-detail-nav">
        <button className="btn btn-outline btn-sm" onClick={onBack}>← History</button>
        <span className="history-detail-date">
          {formatDate(date)} at {formatTime(date)}
        </span>
        <button className="btn btn-outline btn-sm" onClick={onHome}>🏠 Home</button>
      </div>

      {/* Score header */}
      <div className="results-top" style={{ background: meta.lightColor, borderColor: meta.borderColor }}>
        <div className={`results-badge ${passed ? "pass" : "fail"}`}>
          {passed ? "✓ PASS" : "✗ FAIL"}
        </div>
        <div className="results-section-info">
          <span className="results-icon">{meta.icon}</span>
          <span className="results-section-name" style={{ color: meta.color }}>{section}</span>
          <span className={`results-mode-pill ${mode === "exam" ? "exam-pill" : "practice-pill"}`}>
            {mode === "exam" ? "🎯 Exam" : "📚 Practice"}
          </span>
        </div>
      </div>

      <div className="results-score-row">
        <div className="results-score-big" style={{ color: passed ? "#16a34a" : "#dc2626" }}>{pct}%</div>
        <div className="results-score-detail">
          <div><strong style={{ color: "#16a34a" }}>{correct}</strong> correct</div>
          <div><strong style={{ color: "#dc2626" }}>{incorrect}</strong> incorrect</div>
          {unanswered > 0 && <div><strong>{unanswered}</strong> skipped</div>}
          <div>out of <strong>{total}</strong> total</div>
        </div>
      </div>

      <p className={`results-verdict ${passed ? "pass-text" : "fail-text"}`}>
        {passed
          ? "You passed this session! Review the answers below."
          : `You needed ${PASS_THRESHOLD}% to pass. Study the wrong answers below.`}
      </p>

      {/* Progress bar */}
      <div className="results-gauge">
        <div className="gauge-bar">
          <div className="gauge-fill" style={{ width: `${pct}%`, background: passed ? "#16a34a" : "#dc2626" }} />
          <div className="gauge-threshold" style={{ left: `${PASS_THRESHOLD}%` }} />
        </div>
        <div className="gauge-labels">
          <span>0%</span>
          <span style={{ marginLeft: `${PASS_THRESHOLD - 2}%` }}>80%</span>
          <span style={{ marginLeft: "auto" }}>100%</span>
        </div>
      </div>

      {/* Question grid */}
      <div className="results-breakdown">
        <h4>Question-by-Question Breakdown</h4>
        <div className="breakdown-grid">
          {questions.map((q, i) => {
            const isCorrect = answers[i] === q.answer;
            const skipped   = answers[i] === undefined;
            return (
              <div
                key={q.id}
                className={`breakdown-item ${skipped ? "bk-skipped" : isCorrect ? "bk-correct" : "bk-incorrect"}`}
                title={`Q${i + 1}: ${skipped ? "Skipped" : isCorrect ? "Correct" : "Incorrect"}`}
                onClick={() => {
                  setAnswerFilter("all");
                  setTimeout(() => {
                    document.getElementById(`hd-q-${i}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }, 50);
                }}
                style={{ cursor: "pointer" }}
              >
                <span className="bk-num">{i + 1}</span>
                <span className="bk-icon">{skipped ? "—" : isCorrect ? "✓" : "✗"}</span>
              </div>
            );
          })}
        </div>
        <p className="breakdown-hint">Click any square to jump to that question.</p>
      </div>

      {/* Action buttons */}
      <div className="results-actions">
        <button className="btn btn-secondary" onClick={onRetry}>
          {mode === "exam" ? "🎯 Retry This Exam" : "🔄 Retry Practice"}
        </button>
        <button className="btn btn-outline" onClick={onBack}>← Back to History</button>
      </div>

      {/* Inline answer review */}
      <div className="inline-review">
        <div className="inline-review-header">
          <h3>📋 Answer Review</h3>
          <p className="inline-review-sub">All questions from this session with correct answers and explanations.</p>
        </div>

        <div className="review-filters">
          {[
            { key: "incorrect", label: "✗ Wrong",   count: counts.incorrect, color: "#dc2626" },
            { key: "correct",   label: "✓ Right",   count: counts.correct,   color: "#16a34a" },
            { key: "skipped",   label: "— Skipped", count: counts.skipped,   color: "#9ca3af" },
            { key: "all",       label: "All",        count: counts.all,       color: "#1a56db" },
          ].map(({ key, label, count, color }) => (
            <button
              key={key}
              className={`filter-btn ${answerFilter === key ? "filter-active" : ""}`}
              style={answerFilter === key ? { background: color, borderColor: color } : {}}
              onClick={() => setAnswerFilter(key)}
            >
              {label} <span className="filter-count">{count}</span>
            </button>
          ))}
        </div>

        {filtered.length === 0 ? (
          <div className="empty-filter">
            {answerFilter === "incorrect" ? "🎉 No wrong answers in this session!" :
             answerFilter === "skipped"   ? "✓ No skipped questions." :
                                            "No questions in this category."}
          </div>
        ) : (
          <div className="review-list">
            {filtered.map((q) => {
              const originalIdx = questions.indexOf(q);
              return (
                <div key={q.id} id={`hd-q-${originalIdx}`}>
                  <QuestionCard
                    question={q}
                    index={originalIdx}
                    total={questions.length}
                    selected={answers[originalIdx] ?? -1}
                    onSelect={() => {}}
                    showAnswer={true}
                    flagged={false}
                    onFlag={() => {}}
                    isExam={false}
                  />
                </div>
              );
            })}
          </div>
        )}

        <div className="results-actions" style={{ marginTop: "24px" }}>
          <button className="btn btn-secondary" onClick={onRetry}>
            {mode === "exam" ? "🎯 Retry This Exam" : "🔄 Retry Practice"}
          </button>
          <button className="btn btn-outline" onClick={onBack}>← Back to History</button>
        </div>
      </div>
    </div>
  );
}
